/**
 * Gradient stand-in for cover art.
 *
 * SIMULATED. No image is generated anywhere in the app. The hue pair is derived
 * from a hash of the id, so a given song or pack always gets the same colours.
 *
 * The prototype computed `id % 360` inline at eight sites; that produced NaN once
 * ids became UUID strings, so it now goes through idHue(). The lightness and
 * saturation values differ per site in the original and those differences are
 * preserved via the `depth`/`saturation` props rather than flattened to one value.
 * They look arbitrary because they are: the numbers ARE the design.
 */
import type { CSSProperties, ReactNode } from 'react';
import { idHue } from '../../lib/hash';

/**
 * Which lightness pair to use, matching the prototype's per-context values.
 *
 *   card     library song cards            22% → 14%
 *   hero     Studio hero, Vault drawer     24% → 12%
 *   packCard Vault pack grid + featured    22% → 12%
 */
export type ArtworkDepth = 'card' | 'hero' | 'packCard';

export interface ArtworkPlaceholderProps {
  /** Song or pack id — the gradient seed. */
  id: string;
  /**
   * Height in px. Omitted by the Vault's featured hero, which is sized by its own
   * padded content rather than fixed.
   */
  height?: number;
  depth?: ArtworkDepth;
  /**
   * Saturation percentage, or a `[from, to]` pair.
   *
   * A pair is needed because the Vault's featured hero and pack drawer fade 50% →
   * 45% while every other gradient holds a flat 45%.
   */
  saturation?: number | [number, number];
  /**
   * Multiplier for the second hue.
   *
   * The prototype used 7 for songs and 3 for Vault packs, giving the two areas
   * visibly different gradient spreads. That difference is intentional and is
   * preserved rather than unified.
   */
  hueMultiplier?: number;
  /** Overlaid content, e.g. the play badge or a status pill. */
  children?: ReactNode;
  style?: CSSProperties;
}

const DEPTHS: Record<ArtworkDepth, [number, number]> = {
  card: [22, 14],
  hero: [24, 12],
  packCard: [22, 12],
};

export default function ArtworkPlaceholder({
  id,
  height,
  depth = 'card',
  saturation = 45,
  hueMultiplier = 7,
  children,
  style,
}: ArtworkPlaceholderProps) {
  const [lightFrom, lightTo] = DEPTHS[depth];
  const [satFrom, satTo] = Array.isArray(saturation) ? saturation : [saturation, saturation];
  return (
    <div
      style={{
        height,
        position: 'relative',
        background: `linear-gradient(135deg, hsl(${idHue(id)}, ${satFrom}%, ${lightFrom}%), hsl(${idHue(
          id,
          hueMultiplier
        )}, ${satTo}%, ${lightTo}%))`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
